import Group from 'components/utils/Group'
import Image from 'components/Common/Image/Image'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { dateDifference } from 'lib/datetime/dateUtils'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import useOrderItemsEligibleForCarHireBundle from 'hooks/CarHire/useOrderItemsEligibleForCarHireBundle'
import Heading from 'LuxKit/Typography/Heading'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LayoutContainer from 'components/Common/LayoutContainer'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import CarHireBundledUpcomingOrderCarousel from './CarHireBundledUpcomingOrderCarousel'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { connect } from 'react-redux'
import useOffers from 'hooks/Offers/useOffers'
import { getLoyaltyBonusPointsCampaignTypes } from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import { LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL } from 'loyalty/constants/loyaltyBonusPoints'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import { FormattedMessage, useIntl } from 'react-intl'

const MainContainer = styled(Group)`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr auto;
    column-gap: ${rem(24)};
    row-gap: ${rem(32)};
  }
`

const HotelContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr ${rem(68)};
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(164)} 1fr;
    gap: ${rem(24)};
  }
`

const HotelImageWrapper = styled.div`
  grid-column-start: 2;
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-column-start: 1;
  }
`

const HotelContentWrapper = styled(Group)`
  grid-column-start: 1;
  grid-row: 1;
  
  ${mediaQueryUp.tablet} {
    grid-column-start: 2;
  }
`

const DaysToGoLabel = styled(StructureLabel)`
  text-transform: uppercase;
`

const ViewAllButtonWrapper = styled(Group)`
  align-self: end;
  padding: ${rem(10)} ${rem(12)};

  ${mediaQueryUp.tablet} {
    padding: 0;
    grid-column-start: 2;
    grid-row: 1;
  }
`

interface Props {
  order: App.Order
}

interface MappedProps {
  isFetchingCarousel?: boolean
}

function CarHireBundledUpcomingOrder(props: Props & MappedProps) {
  const { order, isFetchingCarousel } = props

  const { isEligible, earliestItem, latestItem, searchLink, fetching: isFetchingBundleEligibility, benefits, loyaltyBonusPointsMultiplier } = useOrderItemsEligibleForCarHireBundle(order.id)

  const offerId = useMemo(() => {
    if (!earliestItem?.orderItem || !('offer' in earliestItem.orderItem) || !('id' in earliestItem.orderItem.offer)) return
    return earliestItem.orderItem.offer.id
  }, [earliestItem])

  const [offers, isFetchingOffers] = useOffers(offerId ? [offerId] : [])
  const offer = offers[0]

  const offerDetails = useMemo(() => {
    if (!offer) return

    const name = offer.name
    let image: App.Image | undefined

    if ('image' in offer) {
      image = offer.image
    } else if ('images' in offer) {
      image = offer.images[0]
    }

    return { image, name }
  }, [offer])

  const daysToGo = useMemo(() => {
    if (!earliestItem) return 0
    return dateDifference(new Date(earliestItem.checkIn), new Date()).days
  }, [earliestItem])

  const dispatch = useAppDispatch()

  const onSearch = useCallback(() => {
    dispatch(pushWithRegion(searchLink))
  }, [searchLink, dispatch])

  const onManageBooking = useCallback(() => {
    dispatch(pushWithRegion(`/account/my-escapes/order/${order.id}`))
  }, [dispatch, order.id])

  const intl = useIntl()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const bonusPointsCampaigns = getLoyaltyBonusPointsCampaignTypes({
    bonusPoints: loyaltyBonusPointsMultiplier ?? 0,
    basePoints: 1,
    bonusStatusCredits: 0,
    statusCredits: 0,
  })
  const bonusPointsCampaign = bonusPointsCampaigns[0]
  const bonusPointsCampaignLabel = bonusPointsCampaign ? intl.formatMessage(LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL[bonusPointsCampaign]) : undefined
  const hasBonusPoints = benefits?.has('loyalty_bonus_points')

  const daysToGoLabel = intl.formatMessage(
    {
      id: 'XYsMI0Bu09',
      defaultMessage: '{daysToGo, plural, one {# day} other {# days}} to go',
      description: 'Label showing how many days until the car hire trip starts',
    },
    { daysToGo },
  )

  const manageBookingLabel = intl.formatMessage({
    id: '8jK84sypKK',
    defaultMessage: 'Manage booking',
    description: 'Link text to manage the hotel booking associated with car hire',
  })

  const viewAllOptionsLabel = intl.formatMessage({
    id: 'BJ+DXaCcZN',
    defaultMessage: 'View all options',
    description: 'Button text to view all car hire options for the trip',
  })

  const isFetching = isFetchingCarousel || isFetchingOffers
  if (!isFetchingBundleEligibility && !isEligible) return null

  return <>
    <LayoutContainer>
      <MainContainer direction="vertical" gap={20} desktopGap={32}>
        {isFetching && <HotelContainer>
          <HotelImageWrapper>
            <CSSBreakpoint min="desktop">
              <LoadingBox style={{ width: 164, height: 88 }} />
            </CSSBreakpoint>
            <CSSBreakpoint max="tablet">
              <LoadingBox style={{ width: 68, height: 68 }} />
            </CSSBreakpoint>
          </HotelImageWrapper>
          <HotelContentWrapper direction="vertical" gap={4} desktopGap={8}>
            <LoadingBox style={{ width: 100, height: 24 }} />
            <LoadingBox style={{ width: 200, height: 28 }} />
            <LoadingBox style={{ width: 100, height: 20 }} />
          </HotelContentWrapper>
        </HotelContainer>}
        {!isFetching && !!earliestItem && !!latestItem && <HotelContainer>
          <HotelImageWrapper>
            <CSSBreakpoint min="desktop">
              <Image id={offerDetails?.image?.id} width={164} height={88} />
            </CSSBreakpoint>
            <CSSBreakpoint max="tablet">
              <Image id={offerDetails?.image?.id} width={68} height={68} />
            </CSSBreakpoint>
          </HotelImageWrapper>
          <HotelContentWrapper direction="vertical" gap={4} desktopGap={8}>
            <LabelGroup>
              {daysToGo > 0 && <DaysToGoLabel variant="promotion">
                {daysToGoLabel}
              </DaysToGoLabel>}
              {hasBonusPoints && !!bonusPointsCampaignLabel && <LoyaltyTierPill kind="primary" tier={tier}>{bonusPointsCampaignLabel}</LoyaltyTierPill>}
            </LabelGroup>
            <Heading variant="heading3">
              {offerDetails?.name}
            </Heading>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <LineCalendarIcon size="S" />
              <BodyText variant="medium">
                <FormattedMessage
                  id="u0Hl7U7egU"
                  defaultMessage="{checkIn} - {checkOut}"
                  description="Date range for car hire bundled upcoming order"
                  values={{
                    checkIn: <FormatDateTime value={earliestItem.checkIn} format="day-month-name" />,
                    checkOut: <FormatDateTime value={latestItem.checkOut} format="day-month-name" />,
                  }}
                />
              </BodyText>
              <BodyText variant="medium">{intl.formatMessage(middleDotSeparatorMessage)}</BodyText>
              <TextLink size="medium" onClick={onManageBooking}>
                {manageBookingLabel}
              </TextLink>
            </Group>
          </HotelContentWrapper>
        </HotelContainer>}
        {!isFetching && <CSSBreakpoint min="desktop">
          <ViewAllButtonWrapper direction="horizontal" horizontalAlign="end" verticalAlign="baseline">
            <TextButton kind="tertiary" size="medium" onClick={onSearch}>
              {viewAllOptionsLabel}
            </TextButton>
          </ViewAllButtonWrapper>
        </CSSBreakpoint>}
      </MainContainer>
    </LayoutContainer>
    <CarHireBundledUpcomingOrderCarousel order={order} />
    {!isFetching && <CSSBreakpoint max="tablet">
      <ViewAllButtonWrapper direction="horizontal" horizontalAlign="end" verticalAlign="baseline">
        <TextButton kind="tertiary" size="medium" onClick={onSearch}>
          {viewAllOptionsLabel}
        </TextButton>
      </ViewAllButtonWrapper>
    </CSSBreakpoint>}
  </>
}

function mapStateToProps(state: App.State, ownProps: Props): MappedProps {
  const listKey = Object.keys(state.carHire.offerLists).find((key) => key.includes(ownProps.order.id))

  return {
    isFetchingCarousel: listKey ? state.carHire.offerLists[listKey].fetching : false,
  }
}

export default connect(mapStateToProps)(CarHireBundledUpcomingOrder)
